import styles from "./MapUIOverlay.module.css";
import { usePosterProject } from "../../model/poster-project-provider";
import { useRef, useEffect, useState, useMemo } from "react";
import { OverlayToolbox } from "./overlay-toolbox";
import { MapElement } from "PosterEditor/utils/canvas-drawing";
import { Rect } from "PosterEditor/model/poster-types";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { ContextType as EditingContextType } from "./overlay-toolbox";
import { Point } from "PosterEditor/model/poster-types";
import { Element as ProjectElement } from "PosterEditor/model/poster-types";
import {
  Backdrop,
  MapLabel,
  StaticLabel,
  Gallery,
  GalleryCell,
} from "PosterEditor/model/poster-project";
import {
  findViewportInCells,
  calculateViewportPhysicalSizeMM,
  calculateRequiredResolution,
  calculateMaxResolution,
  validateImageResolution,
  getImageDimensions,
} from "PosterEditor/utils/gallery-utils";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import { useToast } from "BEDROCK/shared-components/toast";
import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { FloatingTooltip, useTooltip, TOOLTIPS } from "PosterEditor/tooltips";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { SaveImageConfig } from "entity-store/image-store/use-image-store-remote";
import { v4 as uuid } from "uuid";
import { ViewportToolbar } from "./viewport-toolbar";
import { ViewportLoadingOverlay } from "./viewport-loading-overlay/ViewportLoadingOverlay";
import { MobileViewportOverlay } from "./MobileViewportOverlay/MobileViewportOverlay";
import { useTouchMode } from "BEDROCK/mobile";

interface MapUIOverlayProps {
  pointerLocation: { x: number; y: number } | undefined;
  isDragging: boolean;
  map: React.MutableRefObject<any>;
  elementUnderCursor: MapElement | undefined;
  onShowElementEditor: (element: MapElement | undefined) => void;
  isMobile?: boolean;
  // Callback to expose the viewport upload trigger for direct invocation from touch handlers
  // (iOS requires file input clicks to happen in the direct user gesture call stack)
  onExposeViewportUploadTrigger?: (trigger: (viewportId: string, viewportRect?: Rect, zoomAfterUpload?: boolean) => void) => void;
}

export const MapUIOverlay = (props: MapUIOverlayProps) => {
  const isTouchMode = useTouchMode();
  const {
    project,
    styledPointAtCoordinate,
    addNewStaticLabel,
    addNewStyledPoint,
    addNewBackdrop,
    gpsSource,
    isTemplateEditing,
    setGallery,
    cacheImageForGalleryViewport,
    splitViewport,
    deleteViewport,
    updateViewportContent,
  } = usePosterProject();
  const { editorUIState, setSelectedElement, setEditingElement, setSelectedViewportId, setFocusedViewport, setPendingPickerViewport } =
    useEditorUI();
  const { activeTooltipId, dismissTooltip } = useTooltip();
  const { resizeAndSaveImage, deleteImage } = useImageStore();
  const { sizeInCMFromProject, screenSizeFromProject } = useSizePresets();
  const { showToast } = useToast();
  const [
    selectedPointAwaitingEditingContext,
    setSelectedPointAwaitingEditingContext,
  ] = useState<MapElement | undefined>(undefined);

  // State for gallery viewport image upload
  const [pendingViewportId, setPendingViewportId] = useState<string | undefined>(undefined);
  const [uploadingViewportRect, setUploadingViewportRect] = useState<Rect | undefined>(undefined);
  const viewportFileInputRef = useRef<HTMLInputElement>(null);
  // LHS-241: Track if we should zoom to viewport after upload (for empty viewport tap flow)
  const [shouldZoomAfterUpload, setShouldZoomAfterUpload] = useState(false);

  // State for tracking selected viewport's rect for toolbar positioning
  const [selectedViewportRect, setSelectedViewportRect] = useState<Rect | undefined>(undefined);

  const containerRef = useRef<any>(null);

  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const [mapRect, setMapRect] = useState<Rect>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  // State to track window dimensions for mobile viewport overlay positioning
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Handle window resize (device rotation)
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateMeasurements = () => {
      setSize({
        width: element.offsetWidth,
        height: element.offsetHeight,
      });
      const rect = element.getBoundingClientRect();
      setMapRect({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    };

    // Initial measurement
    updateMeasurements();

    // Observe size/position changes
    const resizeObserver = new ResizeObserver(() => {
      updateMeasurements();
    });
    resizeObserver.observe(element);

    window.addEventListener("resize", updateMeasurements);

    // Cleanup observer on component unmount
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateMeasurements);
    };
  }, []);

  // LHS-257: Prevent selection of static labels, backdrops, and map labels on mobile
  const canSelectStaticLabels =
    !isTouchMode &&
    (isTemplateEditing ||
      Object.values(project.editingPermissions.staticLabel).some((v) => v));

  const canSelectBackdrops =
    !isTouchMode &&
    (isTemplateEditing ||
      Object.values(project.editingPermissions.backdrop).some((v) => v));

  const canSelectMapLabels =
    !isTouchMode &&
    (isTemplateEditing ||
      Object.values(project.editingPermissions.mapLabel).some((v) => v));

  const canUploadToGalleryViewport =
    isTemplateEditing || project.editingPermissions.gallery.uploadImage;

  // Helper to update a viewport's imageID within the gallery's nested cell structure
  const updateViewportImageInCells = (
    cells: GalleryCell[],
    viewportId: string,
    newImageID: string
  ): GalleryCell[] => {
    return cells.map((cell) => ({
      ...cell,
      items: cell.items.map((item) => {
        if (item.type === "viewport" && item.viewport.id === viewportId) {
          return {
            ...item,
            viewport: { ...item.viewport, imageID: newImageID },
          };
        } else if (item.type === "cell") {
          return {
            ...item,
            cell: {
              ...item.cell,
              items: updateViewportImageInCells([item.cell], viewportId, newImageID)[0].items,
            },
          };
        }
        return item;
      }),
    }));
  };

  // Handle file upload for gallery viewport
  const handleViewportFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !pendingViewportId || !project.gallery) {
      // Reset state if user cancelled or conditions not met
      setPendingViewportId(undefined);
      setUploadingViewportRect(undefined);
      return;
    }

    // Find the existing viewport to check if it has an image to delete
    const existingViewport = findViewportInCells(
      project.gallery.content,
      pendingViewportId
    );
    const existingImageID = existingViewport?.imageID;

    // Calculate required resolution based on viewport physical size
    const posterSizeCM = sizeInCMFromProject(project);
    let maxResolution = 600; // Default fallback

    if (posterSizeCM) {
      const posterSizeMM = {
        width: posterSizeCM.width * 10,
        height: posterSizeCM.height * 10,
      };

      const viewportSizeMM = calculateViewportPhysicalSizeMM(
        posterSizeMM,
        project.gallery,
        pendingViewportId
      );

      if (viewportSizeMM) {
        const requiredRes = calculateRequiredResolution(
          viewportSizeMM.widthMM,
          viewportSizeMM.heightMM,
          200 // Target DPI
        );

        // Get image dimensions to validate
        try {
          const imageDimensions = await getImageDimensions(file);
          const calculatedMaxRes = calculateMaxResolution(
            requiredRes.width,
            requiredRes.height
          );

          const validation = validateImageResolution(
            imageDimensions.width,
            imageDimensions.height,
            requiredRes.width,
            requiredRes.height,
            calculatedMaxRes
          );

          if (!validation.isValid) {
            showToast(
              `Image resolution too low. Minimum required: ${validation.requiredWidth} × ${validation.requiredHeight} pixels`,
              "error",
              6000
            );
            // Reset state and exit
            setPendingViewportId(undefined);
            setUploadingViewportRect(undefined);
            if (viewportFileInputRef.current) {
              viewportFileInputRef.current.value = "";
            }
            return;
          }

          maxResolution = validation.maxResolution;
        } catch (error) {
          console.error("Failed to get image dimensions:", error);
          // Fall back to default resolution on error
        }
      }
    }

    const blob = file;
    const newFileName = uuid();
    // For user uploads, include project ID in path; for templates, just use "gallery"
    const folder = isTemplateEditing ? "gallery" : `projects/${project.id}/gallery`;
    const config: SaveImageConfig = {
      image: blob,
      // Don't pass existingFilename - we'll delete after successful upload
      newFilename: newFileName,
      isPublic: isTemplateEditing,
      folder,
    };

    try {
      await resizeAndSaveImage(config, maxResolution, "jpeg");

      // Update the gallery with the new image
      const updatedContent = updateViewportImageInCells(
        project.gallery.content,
        pendingViewportId,
        newFileName
      );

      const updatedGallery: Gallery = {
        ...project.gallery,
        content: updatedContent,
      };

      setGallery(updatedGallery);
      cacheImageForGalleryViewport(newFileName);

      // LHS-241: Zoom to viewport after upload if triggered from empty viewport tap
      // Also select the viewport so panning works immediately
      if (shouldZoomAfterUpload && uploadingViewportRect && pendingViewportId) {
        setFocusedViewport(pendingViewportId, uploadingViewportRect);
        setSelectedViewportId(pendingViewportId);
        setShouldZoomAfterUpload(false);
      }

      // Only delete old image after successful upload to prevent data loss
      if (existingImageID) {
        try {
          await deleteImage(existingImageID, folder, "jpeg", isTemplateEditing);
        } catch (deleteError) {
          // Log but don't fail - the new image is already in place
          console.warn("Failed to delete old gallery image:", deleteError);
        }
      }
    } catch (error) {
      console.error("Failed to upload gallery image:", error);
    } finally {
      // Reset state
      setPendingViewportId(undefined);
      setUploadingViewportRect(undefined);
      setShouldZoomAfterUpload(false);
      // Reset file input so same file can be selected again
      if (viewportFileInputRef.current) {
        viewportFileInputRef.current.value = "";
      }
    }
  };

  // Trigger file picker for viewport image upload
  // zoomAfterUpload: if true, the viewport will be focused and selected after upload completes
  // IMPORTANT: click() must be called FIRST before any setState calls
  // iOS Safari requires the click to be as close to the user gesture as possible
  const triggerViewportImageUpload = (viewportId: string, viewportRect?: Rect, zoomAfterUpload?: boolean) => {
    // Click first - iOS requires this to be immediate in the user gesture call stack
    viewportFileInputRef.current?.click();
    // Then set state for loading overlay and upload handling
    setPendingViewportId(viewportId);
    setUploadingViewportRect(viewportRect);
    if (zoomAfterUpload) {
      setShouldZoomAfterUpload(true);
    }
  };

  // Expose the trigger function to parent components (for iOS which requires
  // file input clicks to happen directly in the user gesture call stack)
  // Note: triggerViewportImageUpload must be in deps to ensure the ref always has the latest function
  useEffect(() => {
    if (props.onExposeViewportUploadTrigger) {
      props.onExposeViewportUploadTrigger(triggerViewportImageUpload);
    }
  }, [props.onExposeViewportUploadTrigger, triggerViewportImageUpload]);

  useEffect(() => {
    if (props.map.current) {
      const handleClick = () => {
        if (project.isTemplateDraft) {
          return;
        }

        setSelectedPointAwaitingEditingContext(undefined);
        setEditingElement(undefined);
        setSelectedElement(undefined);

        const elementUnderCursor = props.elementUnderCursor;

        // Clear viewport selection unless clicking on a gallery viewport
        // In template editing mode, empty viewports (with changeImageAction) should also be selectable
        const isClickingGalleryViewport =
          elementUnderCursor?.type === "gallery" &&
          elementUnderCursor?.viewportId &&
          (!elementUnderCursor?.changeImageAction || isTemplateEditing);
        if (!isClickingGalleryViewport) {
          setSelectedViewportId(undefined);
          setSelectedViewportRect(undefined);
        }
        if (elementUnderCursor) {
          //Find out if there is a styled point at the cursor
          //if so, the toolbox editor will be opened for it in the sidebar.
          //if not, we bring up an option menu under the cursor.
          if (elementUnderCursor.pointCoordinate) {
            const styledPoint = styledPointAtCoordinate(
              elementUnderCursor.pointCoordinate.coordinate
            );

            if (styledPoint) {
              let projectElement: ProjectElement = {
                type: "point",
                point: elementUnderCursor.pointCoordinate.point,
                styledPoint: styledPoint.point,
                mapLabel:
                  elementUnderCursor.type === "mapLabel"
                    ? styledPoint.point.label
                    : undefined,
              };
              setEditingElement(projectElement);
            } else if (
              elementUnderCursor.type === "mapLabel" &&
              elementUnderCursor.element
            ) {
              const label = elementUnderCursor.element as MapLabel;
              let projectElement: ProjectElement = {
                type: "mapLabel",
                id: label.id,
                point: elementUnderCursor.pointCoordinate.point,
                mapLabel: label,
              };
              if (canSelectMapLabels) {
                setEditingElement(projectElement);
              }
            } else {
              setSelectedElement(elementUnderCursor);
              setSelectedPointAwaitingEditingContext(elementUnderCursor);
            }
          } else if (
            elementUnderCursor.type === "staticLabel" &&
            elementUnderCursor.element
          ) {
            const label = elementUnderCursor.element as StaticLabel;
            let projectElement: ProjectElement = {
              type: "staticLabel",
              id: label.id,
              point: {
                x: elementUnderCursor.rect.x,
                y: elementUnderCursor.rect.y,
              },
              staticLabel: label,
            };
            if (canSelectStaticLabels) {
              setEditingElement(projectElement);
            }
          } else if (
            elementUnderCursor.type === "backdrop" &&
            elementUnderCursor.element
          ) {
            if (
              !isTemplateEditing &&
              Object.values(project.editingPermissions.backdrop).every(
                (v) => !v
              )
            ) {
              return;
            }
            const backdrop = elementUnderCursor.element as Backdrop;
            let projectElement: ProjectElement = {
              type: "backdrop",
              id: backdrop.id,
              point: {
                x: elementUnderCursor.rect.x,
                y: elementUnderCursor.rect.y,
              },
              backdrop: backdrop,
            };
            if (canSelectBackdrops) {
              setEditingElement(projectElement);
            }
          } else if (
            elementUnderCursor.type === "gallery" &&
            elementUnderCursor.viewportId &&
            project.gallery
          ) {
            // Note: Split, delete, and zoom buttons are now handled by the HTML ViewportToolbar.
            // Canvas only returns changeImageAction for empty viewports (clicking anywhere triggers upload).

            // For empty viewports in template editing mode, select them instead of uploading
            // This allows showing the toolbar with Add Image and Delete options
            if (elementUnderCursor.changeImageAction) {
              if (isTemplateEditing) {
                // In template editing mode: select the empty viewport to show toolbar
                const clickedViewportId = elementUnderCursor.changeImageAction.viewportId;
                const currentSelectedId = editorUIState.selectedViewportId;

                if (currentSelectedId === clickedViewportId) {
                  // Clicking the same viewport - deselect
                  setSelectedViewportId(undefined);
                  setSelectedViewportRect(undefined);
                } else {
                  // Select this viewport and track its rect
                  setSelectedViewportId(clickedViewportId);
                  setSelectedViewportRect(elementUnderCursor.rect);
                }
                setSelectedElement(undefined);
                setSelectedPointAwaitingEditingContext(undefined);
                return;
              } else if (canUploadToGalleryViewport) {
                // Not template editing: trigger image upload directly
                triggerViewportImageUpload(elementUnderCursor.changeImageAction.viewportId, elementUnderCursor.rect);
                return; // Don't show contextual menu
              }
            }

            // Clicking on a populated viewport (not on +) - toggle selection
            const clickedViewportId = elementUnderCursor.viewportId;
            const currentSelectedId = editorUIState.selectedViewportId;

            if (currentSelectedId === clickedViewportId) {
              // Clicking the same viewport - deselect
              setSelectedViewportId(undefined);
              setSelectedViewportRect(undefined);
            } else {
              // Select this viewport and track its rect
              setSelectedViewportId(clickedViewportId);
              setSelectedViewportRect(elementUnderCursor.rect);
            }
            setSelectedElement(undefined);
            setSelectedPointAwaitingEditingContext(undefined);
          } else {
            setSelectedElement(elementUnderCursor);
            setSelectedPointAwaitingEditingContext(elementUnderCursor);
          }
        } else {
          if (selectedPointAwaitingEditingContext) {
            setSelectedElement(undefined);
            setSelectedPointAwaitingEditingContext(undefined);
          } else if (editorUIState.editingElement) {
            setEditingElement(undefined);
          } else {
            // Nothing is currently selected, so bring up the editing context menu to let the user add
            // a label.

            let mapElement: MapElement = {
              type: "point",
              rect: {
                x: props.pointerLocation?.x ?? 0,
                y: props.pointerLocation?.y ?? 0,
                width: 0,
                height: 0,
              },
            };
            setSelectedElement(mapElement);
            setSelectedPointAwaitingEditingContext(mapElement);
          }
        }
      };

      const updateSelectedPointMenuPosition = () => {
        // if (selectedPoint) {
        //   const selectedCoordinate = selectedPoint.coordinate;
        //   const movedPoint = props.map.current.project([
        //     selectedCoordinate.lng,
        //     selectedCoordinate.lat,
        //   ]);
        //   if (
        //     movedPoint.x < 0 ||
        //     movedPoint.x > size.width ||
        //     movedPoint.y < 0 ||
        //     movedPoint.y > size.height
        //   ) {
        //     setSelectedPoint(undefined);
        //     return;
        //   }
        //   setSelectedPoint({
        //     point: movedPoint,
        //     coordinate: selectedCoordinate,
        //   });
        // }
      };

      const handleMove = () => {
        // setEditingElement(undefined);
        updateSelectedPointMenuPosition();
      };

      const handleZoom = () => {
        updateSelectedPointMenuPosition();
      };

      const handleDrag = () => {
        updateSelectedPointMenuPosition();
      };

      props.map.current.on("move", handleMove);
      props.map.current.on("zoom", handleZoom);
      props.map.current.on("click", handleClick);
      props.map.current.on("drag", handleDrag);

      return () => {
        props.map.current.off("move", handleMove);
        props.map.current.off("zoom", handleZoom);
        props.map.current.off("click", handleClick);
      };
    }
    // Dependency array - ensure only necessary dependencies are here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    size.width,
    size.height,
    props.map,
    gpsSource?.displayedCoordinates,
    props.pointerLocation,
  ]);

  const pointFromRect: Point | null = selectedPointAwaitingEditingContext
    ? {
        x: selectedPointAwaitingEditingContext?.rect.x ?? 0,
        y: selectedPointAwaitingEditingContext?.rect.y ?? 0,
      }
    : null;

  const relativePointFromRect: Point | null = {
    x: (pointFromRect?.x ?? 0) / size.width,
    y: (pointFromRect?.y ?? 0) / size.height,
  };

  const onSelectEditingContextForNewElementAtSelectedPoint = (
    contextType: EditingContextType
  ) => {
    // setEditingElement(selectedPointAwaitingEditingContext);
    // setSelectedElement(selectedPointAwaitingEditingContext);
    const pointCoordinate =
      selectedPointAwaitingEditingContext?.pointCoordinate;

    if (!pointCoordinate && !pointFromRect) {
      return;
    }

    const coord =
      pointCoordinate?.coordinate ??
      (props.map.current.unproject(pointFromRect) as Coordinate);
    const point = pointCoordinate?.point ?? pointFromRect;

    if (contextType === "staticLabel" && relativePointFromRect) {
      const label = addNewStaticLabel(relativePointFromRect, 30 / size.width);
      const mapElement: ProjectElement = {
        type: "staticLabel",
        id: label.id,
        point: relativePointFromRect,
        staticLabel: { ...label },
      };
      setEditingElement(mapElement);
    } else if (contextType === "point" && point) {
      const styledPoint = addNewStyledPoint(coord);
      const mapElement: ProjectElement = {
        type: "point",
        point: point,
        styledPoint: styledPoint,
        mapLabel: styledPoint.label,
      };
      setEditingElement(mapElement);
    } else if (contextType === "backdrop" && relativePointFromRect) {
      const backdrop = addNewBackdrop(relativePointFromRect, "color");
      const mapElement: ProjectElement = {
        type: "backdrop",
        id: backdrop.id,
        backdrop,
      };
      setEditingElement(mapElement);
    } else if (contextType === "photo" && relativePointFromRect) {
      const backdrop = addNewBackdrop(relativePointFromRect, "image");
      const mapElement: ProjectElement = {
        type: "backdrop",
        id: backdrop.id,
        backdrop,
      };
      setEditingElement(mapElement);
      //Open file selector and allow image to be uploaded.
    }
  };

  const point =
    selectedPointAwaitingEditingContext?.pointCoordinate?.point ??
    pointFromRect;

  const showMapLabelsTooltip = activeTooltipId === "map-labels";

  // Update viewport rect when hovering over the selected viewport
  useEffect(() => {
    if (
      editorUIState.selectedViewportId &&
      props.elementUnderCursor?.type === "gallery" &&
      props.elementUnderCursor?.viewportId === editorUIState.selectedViewportId
    ) {
      setSelectedViewportRect(props.elementUnderCursor.rect);
    }
  }, [props.elementUnderCursor, editorUIState.selectedViewportId]);

  // Get selected viewport data for toolbar
  const selectedViewport = editorUIState.selectedViewportId && project.gallery
    ? findViewportInCells(project.gallery.content, editorUIState.selectedViewportId)
    : undefined;

  // Toolbar action handlers
  const handleToolbarSplitHorizontal = () => {
    if (editorUIState.selectedViewportId) {
      splitViewport(editorUIState.selectedViewportId, "horizontal");
    }
  };

  const handleToolbarSplitVertical = () => {
    if (editorUIState.selectedViewportId) {
      splitViewport(editorUIState.selectedViewportId, "vertical");
    }
  };

  const handleToolbarReplaceImage = () => {
    if (editorUIState.selectedViewportId) {
      triggerViewportImageUpload(editorUIState.selectedViewportId, selectedViewportRect);
    }
  };

  const handleToolbarZoomIn = () => {
    if (editorUIState.selectedViewportId && selectedViewport) {
      const currentScale = selectedViewport.contentScale ?? 1.0;
      const newScale = Math.min(5.0, currentScale + 0.25);
      updateViewportContent(
        editorUIState.selectedViewportId,
        selectedViewport.contentOffset ?? { x: 0, y: 0 },
        newScale
      );
    }
  };

  const handleToolbarZoomOut = () => {
    if (editorUIState.selectedViewportId && selectedViewport) {
      const currentScale = selectedViewport.contentScale ?? 1.0;
      const newScale = Math.max(1.0, currentScale - 0.25);
      // Reset offset to center when zooming out to scale 1.0
      const newOffset = newScale <= 1.0 ? { x: 0, y: 0 } : (selectedViewport.contentOffset ?? { x: 0, y: 0 });
      updateViewportContent(
        editorUIState.selectedViewportId,
        newOffset,
        newScale
      );
    }
  };

  const handleToolbarDelete = () => {
    if (editorUIState.selectedViewportId) {
      deleteViewport(editorUIState.selectedViewportId);
      setSelectedViewportId(undefined);
      setSelectedViewportRect(undefined);
    }
  };

  // Get focused viewport data for mobile overlay
  const focusedViewport = editorUIState.focusedViewportId && project.gallery
    ? findViewportInCells(project.gallery.content, editorUIState.focusedViewportId)
    : undefined;

  // Calculate focused viewport screen rect for mobile overlay positioning
  // This replicates the focus transform calculation from PosterStage.tsx
  const focusedViewportScreenRect = useMemo(() => {
    if (!editorUIState.focusedViewportId || !editorUIState.focusedViewportRect) return undefined;

    const navbarHeight = 60;
    const margin = 16;
    const viewportWidth = windowSize.width;
    const viewportHeight = windowSize.height;

    const availableWidth = viewportWidth - margin * 2;
    const availableHeight = viewportHeight - navbarHeight - margin * 2;

    const focusedRect = editorUIState.focusedViewportRect;
    const scaleToFitWidth = availableWidth / focusedRect.width;
    const scaleToFitHeight = availableHeight / focusedRect.height;
    const targetScale = Math.min(scaleToFitWidth, scaleToFitHeight, 1);

    const scaledWidth = focusedRect.width * targetScale;
    const scaledHeight = focusedRect.height * targetScale;

    // Calculate viewport center in poster coordinates (matches PosterStage.tsx)
    const viewportCenterX = focusedRect.x + focusedRect.width / 2;
    const viewportCenterY = focusedRect.y + focusedRect.height / 2;

    // Screen center position
    const screenCenterX = viewportWidth / 2;
    const screenCenterY = navbarHeight + availableHeight / 2 + margin;

    // Calculate where viewport center ends up in scaled coordinates
    const scaledViewportCenterX = viewportCenterX * targetScale;
    const scaledViewportCenterY = viewportCenterY * targetScale;

    // Account for poster centering offset (matches PosterStage.tsx logic)
    const screenSize = screenSizeFromProject(project) ?? { width: 0, height: 0 };
    const scaledPosterWidth = screenSize.width * targetScale;
    const posterLeftOffset = (viewportWidth - scaledPosterWidth) / 2;

    // Calculate translation (matches PosterStage.tsx)
    const translateX = screenCenterX - posterLeftOffset - scaledViewportCenterX;
    const translateY = screenCenterY - scaledViewportCenterY - navbarHeight;

    // Calculate final viewport screen position
    const screenX = posterLeftOffset + focusedRect.x * targetScale + translateX;
    const screenY = navbarHeight + focusedRect.y * targetScale + translateY;

    return { x: screenX, y: screenY, width: scaledWidth, height: scaledHeight };
  }, [editorUIState.focusedViewportId, editorUIState.focusedViewportRect, windowSize, project, screenSizeFromProject]);

  // LHS-241: Open picker directly when empty viewport is tapped (via touch input)
  // Note: This triggers for touch input regardless of isMobile (desktop vs mobile layout)
  useEffect(() => {
    if (
      editorUIState.pendingPickerViewportId &&
      editorUIState.pendingPickerViewportRect &&
      canUploadToGalleryViewport
    ) {
      // Trigger picker immediately and mark that we should zoom after upload
      setShouldZoomAfterUpload(true);
      triggerViewportImageUpload(
        editorUIState.pendingPickerViewportId,
        editorUIState.pendingPickerViewportRect
      );
      // Clear the pending state
      setPendingPickerViewport(undefined, undefined);
    }
  }, [editorUIState.pendingPickerViewportId, editorUIState.pendingPickerViewportRect, canUploadToGalleryViewport, setPendingPickerViewport]);

  // Mobile viewport overlay handlers
  const handleMobileDone = () => {
    setFocusedViewport(undefined, undefined);
  };

  const handleMobileZoomIn = () => {
    if (editorUIState.focusedViewportId && focusedViewport) {
      const currentScale = focusedViewport.contentScale ?? 1.0;
      const newScale = Math.min(5.0, currentScale + 0.25);
      updateViewportContent(
        editorUIState.focusedViewportId,
        focusedViewport.contentOffset ?? { x: 0, y: 0 },
        newScale
      );
    }
  };

  const handleMobileZoomOut = () => {
    if (editorUIState.focusedViewportId && focusedViewport) {
      const currentScale = focusedViewport.contentScale ?? 1.0;
      const newScale = Math.max(1.0, currentScale - 0.25);
      const newOffset = newScale <= 1.0 ? { x: 0, y: 0 } : (focusedViewport.contentOffset ?? { x: 0, y: 0 });
      updateViewportContent(
        editorUIState.focusedViewportId,
        newOffset,
        newScale
      );
    }
  };

  return (
    <div ref={containerRef} className={styles.container} id="map-overlay">
      {/* Hidden file input for gallery viewport image upload */}
      <input
        ref={viewportFileInputRef}
        type="file"
        accept=".png, .jpg, .jpeg"
        onChange={handleViewportFileUpload}
        style={{ display: "none" }}
      />
      {!props.isMobile &&
        editorUIState.selectedElement &&
        !editorUIState.editingElement &&
        point && (
          <OverlayToolbox
            position={point}
            mapRect={mapRect}
            onClose={() => {
              setSelectedPointAwaitingEditingContext(undefined);
            }}
            onSelectContextType={
              onSelectEditingContextForNewElementAtSelectedPoint
            }
          />
        )}
      {showMapLabelsTooltip && !props.isMobile && (
        <FloatingTooltip
          content={TOOLTIPS["map-labels"].content}
          position="left-of-center"
          containerRef={containerRef}
          onDismiss={() => dismissTooltip("map-labels")}
          isVisible={showMapLabelsTooltip}
        />
      )}
      {/* Viewport toolbar for selected gallery viewport */}
      {editorUIState.selectedViewportId &&
        selectedViewportRect &&
        selectedViewport && (
          <ViewportToolbar
            viewportId={editorUIState.selectedViewportId}
            viewportRect={selectedViewportRect}
            mapContainerRect={containerRef.current?.getBoundingClientRect() ?? new DOMRect()}
            isAdmin={isTemplateEditing}
            hasImage={!!selectedViewport.imageID}
            currentScale={selectedViewport.contentScale ?? 1.0}
            onSplitHorizontal={handleToolbarSplitHorizontal}
            onSplitVertical={handleToolbarSplitVertical}
            onReplaceImage={handleToolbarReplaceImage}
            onZoomIn={handleToolbarZoomIn}
            onZoomOut={handleToolbarZoomOut}
            onDelete={handleToolbarDelete}
          />
        )}
      {/* Loading overlay for viewport image upload */}
      {/* On mobile, only show when viewport is focused (overlay position is based on focused zoom level) */}
      {pendingViewportId && uploadingViewportRect && (!props.isMobile || editorUIState.focusedViewportId) && (
        <ViewportLoadingOverlay
          viewportRect={uploadingViewportRect}
          mapContainerRect={containerRef.current?.getBoundingClientRect() ?? new DOMRect()}
        />
      )}
      {/* Mobile viewport overlay for focused viewport */}
      {props.isMobile && editorUIState.focusedViewportId && focusedViewport && (
        <MobileViewportOverlay
          focusedViewportId={editorUIState.focusedViewportId}
          viewportScreenRect={focusedViewportScreenRect}
          hasImage={!!focusedViewport.imageID}
          currentScale={focusedViewport.contentScale ?? 1.0}
          onDone={handleMobileDone}
          onZoomIn={handleMobileZoomIn}
          onZoomOut={handleMobileZoomOut}
        />
      )}
    </div>
  );
};
